import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FetchGstInDetails } from '../../../library/GstinFetch';
import { FinancialYearState, GstDetailsValidationErrors, SaveCompanyPreferenceParams } from '../types';
import { createNumberTrackerSettings } from '../api/voucherSettingData';
import useControlledNavigation from '../../../hooks/useControlledNavigation';
import { FINANCIAL_YEAR_OPTIONS } from '../constants/static';
import { handleCreateFY } from '../utils/handleCreateFY';
import { handleCreateCompanyDetailsAndEntity } from '../utils/handleCreateCompanyDetailsAndEntity';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { fetchUserPreference, FetchUserPreferenceReturn } from '../../../redux/reducers/UserPreference';
import { SaveUserPreferenceMutation } from '../api/SaveUserPreference';
import { resetState } from '../../../redux/reducers/FYSetupStep';
import { fetchCompany, fetchCompanyList } from '../../../redux/reducers/CompanySlice';
import { useCreateCompanyPreferenceMutation, useListCompanyPreferenceQuery } from '../../../redux/apis/companyPreference.api';
import { computeFY } from '../utils/computeFY';
import { CompanyPreference } from '../../../types';
import { getCompanyData, UpdateFinancialYearMutation } from '../api/createFinancialyear.api';
import { setNavigationBlocked } from '../../../redux/reducers/controllNavigation';
import { useLocation } from 'react-router-dom';
import { useFetchDashboardDataQuery } from '../../../redux/apis/dashboardApi';
import { GetCompanyQuery } from '../../../graphql';
import { store } from '../../../redux/store';

type AppDispatch = typeof store.dispatch;
interface LocationState {
  active_step?: number;
}

export const useCreateFinancialYear = () => {
  const navigate = useControlledNavigation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const routerState = (location.state ?? {}) as LocationState;
  const [step, setStep] = useState<number>(routerState?.active_step ?? 0);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('FINANCIAL');
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [fyAlreadyExists, setFyAlreadyExists] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [isCompanyPreferenceCheck, setIsCompanyPreferenceCheck] = useState<boolean>(false);
  const [totalTurnOver, setTotalTurnOver] = useState<number>(0);
  const [enableEInvoice, setEnableEInvoice] = useState<boolean>(false);
  const [companyDetails, setCompanyDetails] = useState<NonNullable<GetCompanyQuery['getCompany']>[0] | null>(null);
  const [userPreference, setUserPreference] = useState<FetchUserPreferenceReturn | null>(null);
  const [gstDetailsValidationErrors, setGstDetailsValidationErrors] = useState<GstDetailsValidationErrors>({
    gstin: '',
    lutNumber: '',
    lutFrom: '',
    lutTo: '',
  });
  const [state, setState] = useState<FinancialYearState>({
    startYear: 0,
    endyear: 0,
    startDate: 0,
    endDate: 0,
    financialYearFromId: 0,
    financialYearToId: 0,
    fyPreview: '',
    companyDetails: null,
    companyEntity: null,
    companyAddressEntity: null,
    transferClosingbalance: false,
  });



  const [formErrors, setFormErrors] = useState<{ startYear?: string }>({});

  const resetFYFromStartYear = () => {
    setState((prev) => ({
      ...prev,
      startYear: 0,
      endyear: 0,
      startDate: 0,
      endDate: 0,
      fyPreview: '',
      transferClosingbalance: false,
    }));
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getCompanyData();
        const response = await dispatch(fetchUserPreference()).unwrap();
        if (mounted) {
          setCompanyDetails(data);
          setUserPreference(response);
        }
      } catch (err) {
        showErrorSnackbar('Unable to load company information. Please try again later.', 'right', 'bottom');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const { data: companyPreference = [], isSuccess } = useListCompanyPreferenceQuery();

  const dashboardVariables =
    state.startDate && state.endDate
      ? {
          startdate: state.startDate,
          enddate: state.endDate,
          openingbalance: 1,
          companyid: state.companyDetails?.companyid,
          financialyearid: state.financialYearFromId,
        }
      : null;

  const areQueryVariablesValid = dashboardVariables
    ? Object.values(dashboardVariables).every((value) => value !== undefined && value !== null)
    : false;
  const {
    data: dashboardApiData,
    isLoading,
    refetch: refetchDashboard,
    isFetching,
  } = useFetchDashboardDataQuery(dashboardVariables, {
    skip: !areQueryVariablesValid,
  });

  const [createCompanyPreference] = useCreateCompanyPreferenceMutation();

  const saveCompanyPreference = async ({ fromFy, toFy, activeStep, startYear, endYear }: SaveCompanyPreferenceParams): Promise<void> => {
    try {
      const userPreferenceConfig = {
        additionalvalue: `${fromFy}-${toFy}-${selectedOption}-${startYear}-${endYear}`,
        attributetypecd: 'NewFY Creation',
        attributevalue: activeStep,
      };

      await createCompanyPreference({
        createCompanyPreferenceInput: userPreferenceConfig,
      });
    } catch (error) {
      throw new Error('Failed to save company preference');
    }
  };

  useEffect(() => {
    if (
      !enableEInvoice &&
      dashboardApiData?.length &&
      state.companyDetails?.einvoiceflag === 0 &&
      state.companyDetails.registrationtype !== 1 &&
      step === 2
    ) {
      const turnover = dashboardApiData.reduce((acc: number, item: { accounttypecd: string; balance: number }) => {
        if (item.accounttypecd === 'BS') {
          return acc + item.balance;
        }
        return acc;
      }, 0);
      setTotalTurnOver(turnover);
      setEnableEInvoice(true);
    }
  }, [dashboardApiData, state]);

  useEffect(() => {
    const applyFinancialYearFallback = () => {
      if (!userPreference?.currentFY?.startyear || !userPreference.currentFY?.financialyearid) return;
      if (!selectedYear) {
        const nextYear = Number(userPreference?.currentFY?.startyear) + 1;
        setSelectedYear(nextYear);
      }
      setState((prev) => ({
        ...prev,
        financialYearFromId: userPreference.currentFY?.financialyearid ?? 0,
      }));
    };

    if (!companyPreference?.length) {
      applyFinancialYearFallback();
      return;
    }
    if (isSuccess) {
      setIsCompanyPreferenceCheck(true);
    }

    const newFYPref = companyPreference.find(
      (pref: CompanyPreference) => pref.attributetypecd === 'NewFY Creation' && Number(pref.attributevalue) > 1
    );

    if (!newFYPref) {
      applyFinancialYearFallback();
      return;
    }

    const savedStep = Number(newFYPref.attributevalue);
    const [fromStr, toStr, option, startStr] = newFYPref?.additionalvalue?.split('-') || '';
    const fromFy = Number(fromStr);
    const toFy = Number(toStr);
    const startYear = Number(startStr);

    const toFYExists = userPreference?.listFY?.some((f) => f.financialyearid === toFy);

    if (toFYExists) {
      dispatch(setNavigationBlocked(true));
      setStep(savedStep);
      setState((prev) => ({
        ...prev,
        financialYearFromId: fromFy,
        financialYearToId: toFy,
      }));
      setSelectedYear(startYear);
      setSelectedOption(option);
      dispatch(setNavigationBlocked(true));
    } else {
      applyFinancialYearFallback();
    }
  }, [companyPreference, userPreference, dispatch]);

  useEffect(() => {
    if (!selectedYear) {
      resetFYFromStartYear();
      setFormErrors({ startYear: 'Start year required' });
      return;
    }

    setFormErrors((prevErrors) => ({ ...prevErrors, startYear: '' }));
    if (!userPreference?.currentFY?.financialyearid || !userPreference.currentFY?.startyear) return;
    const startYear = selectedYear;
    const isImmediateNextYear = Number(userPreference.currentFY?.startyear) + 1 === selectedYear;
    const endYear = selectedOption === 'CALENDAR' ? startYear : startYear + 1;
    const { startDateTimestamp, endDateTimestamp, fyPreview } = computeFY(selectedOption, startYear);
    setState((prev) => ({
      ...prev,
      startYear,
      endyear: endYear,
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      fyPreview,
      companyDetails: companyDetails?.relatedcompanydetailsnew?.[0] ?? prev.companyDetails ?? null,
      companyEntity: companyDetails?.relatedcompanyentity?.[0] ?? prev.companyEntity ?? null,
      companyAddressEntity: companyDetails?.relatedaddressentity?.[0] ?? prev.companyAddressEntity ?? null,
      emailContact: companyDetails?.relatedemailcontacts?.[0] ?? prev.emailContact ?? null,
      phoneContacts: companyDetails?.relatedphonecontacts?.[0] ?? prev.phoneContacts ?? null,
      transferClosingbalance: isImmediateNextYear,
    }));
  }, [selectedOption, selectedYear, userPreference, companyDetails]);

  useEffect(() => {
    if (step ===1) {
      const existingFY = userPreference?.listFY?.find(
        (f) => Number(f.startyear) === state.startYear && Number(f.endyear) === state.endyear 
      );
      if (existingFY) {
        setFyAlreadyExists(true);
        setError('Financial year already exists with the selected start and end year.');
        return;
      } else {
        setFyAlreadyExists(false);
        setError('');
      }
    }
  }, [userPreference?.listFY, state.startYear, state.endyear, step]);

  const next = useCallback(async () => {
    if (step >= 2 && !state.financialYearToId) {
      return;
    }
    if (step === 1) {
      if (!state.startYear || !state.endyear) {
        showErrorSnackbar('Please select a start year to continue');
        return;
      }
      if (!state.financialYearFromId || !userPreference?.currentFY) {
        setError('Please wait a moment while we load your data. Thank you for your patience.');
        return;
      }
      if (fyAlreadyExists) return;
      setButtonLoading(true);
      const response = await handleCreateFY(state, userPreference?.currentFY, setError);
      if (!response.success || !userPreference?.currentFY?.financialyearid) {
        setButtonLoading(false);
        return;
      }
      if (response.financialYearId) {
        dispatch(setNavigationBlocked(true));
        await saveCompanyPreference({
          fromFy: userPreference?.currentFY?.financialyearid,
          toFy: response.financialYearId,
          activeStep: step + 1,
          startYear: state.startYear,
          endYear: state.endyear,
        });
        await UpdateFinancialYearMutation({
          updatefinancialyearstepsInput: [
            {
              financialyearid: response.financialYearId,
              stepscompleted: JSON.stringify(['step 1']),
            },
          ],
        });
        setState((prev) => ({
          ...prev,
          financialYearToId: response.financialYearId ?? 0,
        }));
        setButtonLoading(false);
      }
    }

    if (step === 2) {
      if (!validateGSTDetails()) {
        return;
      }

      const success = await handleCreateCompanyDetailsAndEntity(
        state,
        setButtonLoading,
        setGstDetailsValidationErrors,
        gstDetailsValidationErrors
      );
      if (!success) return;
      setLoading(true);
      await createNumberTrackerSettings({
        financialyearid: state.financialYearToId,
        companyid: companyDetails?.companyid ?? 0,
        regType: state?.companyDetails?.registrationtype ?? 0,
      });
      setLoading(false);
      await saveCompanyPreference({
        fromFy: state.financialYearFromId,
        toFy: state.financialYearToId,
        activeStep: step + 1,
        startYear: state.startYear,
        endYear: state.endyear,
      });
      await UpdateFinancialYearMutation({
        updatefinancialyearstepsInput: [
          {
            financialyearid: state.financialYearToId,
            stepscompleted: JSON.stringify(['step 1', 'step 2']),
          },
        ],
      });
    }

    setStep((s) => Math.min(s + 1, 3));
  }, [step, fyAlreadyExists, state, dispatch]);

  const prev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);
  const goTo = useCallback((idx: number) => setStep(idx), []);
  const handleCancel = (params: { force: boolean }) => navigate('/', { force: params.force });

  const handleChangeGSTDetails = async (event: React.ChangeEvent<HTMLInputElement>) => {

    const { name, value, checked } = event.target;
    if (name === 'gstin') {
      const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      setGstDetailsValidationErrors((prev) => ({ ...prev, gstin: '' }));

      if (value && state.companyDetails?.registrationtype !== 1) {
        if (value.length !== 15) {
          setGstDetailsValidationErrors((prev) => ({
            ...prev,
            gstin: 'GSTIN must be exactly 15 characters',
          }));
        } else if (!gstinPattern.test(value)) {
          setGstDetailsValidationErrors((prev) => ({
            ...prev,
            gstin: 'Please enter a valid GSTIN format',
          }));
        }
      }

      setState((prev) => ({
        ...prev,
        companyEntity: prev.companyEntity ? { ...prev.companyEntity, gstin: value } : null,
      }));

      if (gstinPattern.test(value)) {
        setLoading(true);
        const fetchData = await FetchGstInDetails(value);
        const typeMap: Record<string, number> = {
          Regular: 2,
          'SEZ Unit': 3,
          Composition: 4,
        };

        const regType = typeMap[fetchData?.data?.TaxPayerType];
        if (regType) {
          setState((prev) => ({
            ...prev,
            companyDetails: prev.companyDetails ? { ...prev.companyDetails, registrationtype: regType } : null,
          }));
        }
        setLoading(false);
      }
    } else if (name === 'einvoiceflag') {
      setState((prev) => ({
        ...prev,
        companyDetails: prev.companyDetails ? { ...prev.companyDetails, einvoiceflag: checked ? 1 : 0 } : null,
      }));
    } else if (name === 'registrationtype') {
      setState((prev) => ({
        ...prev,
        companyDetails: prev.companyDetails ? { ...prev.companyDetails, [name]: Number(value) } : null,
      }));
      if (value === '1') {
        setGstDetailsValidationErrors((prev) => ({ ...prev, gstin: '' }));
      }
    } else if (name === 'lutbondvalidfrom' || name === 'lutbondtodate') {
      const dateValue = Number(value);
      setState((prev) => ({
        ...prev,
        companyDetails: prev.companyDetails ? { ...prev.companyDetails, [name]: dateValue } : null,
      }));
      
      if (name === 'lutbondvalidfrom') {
        setGstDetailsValidationErrors((prev) => ({ ...prev, lutFrom: '' }));
      } else if (name === 'lutbondtodate') {
        setGstDetailsValidationErrors((prev) => ({ ...prev, lutTo: '' }));
      }
      
      if (name === 'lutbondtodate' && state.companyDetails?.lutbondvalidfrom && dateValue) {
        if (dateValue <= state.companyDetails.lutbondvalidfrom) {
          setGstDetailsValidationErrors((prev) => ({
            ...prev,
            lutTo: 'Valid To date must be after Valid From date.',
          }));
        }
      }
      
      if (state.companyDetails?.lut && (name === 'lutbondvalidfrom' || name === 'lutbondtodate')) {
        if (name === 'lutbondvalidfrom' && !dateValue) {
          setGstDetailsValidationErrors((prev) => ({
            ...prev,
            lutFrom: 'Valid From date is required when LUT number is provided.',
          }));
        } else if (name === 'lutbondtodate' && !dateValue) {
          setGstDetailsValidationErrors((prev) => ({
            ...prev,
            lutTo: 'Valid To date is required when LUT number is provided.',
          }));
        }
      }
    } else {
      setState((prev) => ({
        ...prev,
        companyDetails: prev.companyDetails ? { ...prev.companyDetails, [name]: value } : null,
      }));
    }
  };

  const handleOnBlurGSTDetails = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'lut') {
      const lutPattern = /^[A-Z0-9]{15}$/;
      setGstDetailsValidationErrors((prev) => ({ ...prev, lutNumber: '' }));
      
      if (value && !lutPattern.test(value)) {
        setGstDetailsValidationErrors((prev) => ({
          ...prev,
          lutNumber: 'Please enter a valid 15-character LUT Number (alphanumeric).',
        }));
      } else if (value && lutPattern.test(value)) {
        setState((prev) => ({
          ...prev,
          companyDetails: prev.companyDetails
            ? {
                ...prev.companyDetails,
                lutbondvalidfrom: prev.companyDetails.lutbondvalidfrom === 0 ? prev.startDate : prev.companyDetails.lutbondvalidfrom,
                lutbondtodate: prev.companyDetails.lutbondtodate === 0 ? prev.endDate : prev.companyDetails.lutbondtodate,
              }
            : null,
        }));
        
        setGstDetailsValidationErrors((prev) => ({
          ...prev,
          lutNumber: '',
          lutFrom: '',
          lutTo: '',
        }));
      } else {
        setState((prev) => ({
          ...prev,
          companyDetails: prev.companyDetails
            ? {
                ...prev.companyDetails,
                lut: '',
                lutbondvalidfrom: 0,
                lutbondtodate: 0,
              }
            : null,
        }));
        setGstDetailsValidationErrors((prev) => ({
          ...prev,
          lutFrom: '',
          lutTo: '',
        }));
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    await saveCompanyPreference({
      fromFy: 0,
      toFy: 0,
      activeStep: 0,
      startYear: 0,
      endYear: 0,
    });
    await UpdateFinancialYearMutation({
      updatefinancialyearstepsInput: [
        {
          financialyearid: state.financialYearToId,
          stepscompleted: JSON.stringify(['step 1', 'step 2', 'step 3']),
        },
      ],
    });

    showSuccessSnackbar('Financial year created successfully');
    setLoading(false);
    if (userPreference?.currentFY?.financialyearid !== state.financialYearToId) {
      setConfirmationModal(true);
    } else {
      handleCancel({ force: false });
    }
  };

  const handleConfirmOrCancel = async (action: string) => {
    try {
      setLoading(true);

      if (action === 'confirm' && companyDetails?.companyid && state.financialYearToId) {
        await SaveUserPreferenceMutation({
          companyid: companyDetails?.companyid,
          financialyearid: state.financialYearToId,
        });
        showSuccessSnackbar('Financial Year successfully created and switched!');
      }

      handleCancel({ force: true });
      dispatch({ type: 'resetApi' });
      dispatch(resetState());
      dispatch(fetchUserPreference() as any);
      dispatch(fetchCompanyList() as any);
      dispatch(fetchCompany() as any);
      dispatch(setNavigationBlocked(false));
    } finally {
      setLoading(false);
      resetAll();
    }
  };

  const validateGSTDetails = (): boolean => {
    const errors: GstDetailsValidationErrors = {
      gstin: '',
      lutNumber: '',
      lutFrom: '',
      lutTo: '',
    };

    if (state.companyDetails?.registrationtype !== 1 && state.companyEntity?.gstin) {
      const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      
      if (state.companyEntity.gstin.length !== 15) {
        errors.gstin = 'GSTIN must be exactly 15 characters';
      } else if (!gstinPattern.test(state.companyEntity.gstin)) {
        errors.gstin = 'Please enter a valid GSTIN format';
      }
    }

    if (state.companyDetails?.lut) {
      const lutPattern = /^[A-Z0-9]{15}$/;
      if (!lutPattern.test(state.companyDetails.lut)) {
        errors.lutNumber = 'Please enter a valid 15-character LUT Number (alphanumeric).';
      } else {
        if (!state.companyDetails.lutbondvalidfrom) {
          errors.lutFrom = 'Valid From date is required when LUT number is provided.';
        }
        if (!state.companyDetails.lutbondtodate) {
          errors.lutTo = 'Valid To date is required when LUT number is provided.';
        }
        if (state.companyDetails.lutbondvalidfrom && state.companyDetails.lutbondtodate) {
          if (state.companyDetails.lutbondtodate <= state.companyDetails.lutbondvalidfrom) {
            errors.lutTo = 'Valid To date must be after Valid From date.';
          }
        }
      }
    }

    setGstDetailsValidationErrors(errors);
    
    const hasGstinLengthError = errors.gstin === 'GSTIN must be exactly 15 characters';
    const hasLutErrors = errors.lutNumber !== '' || errors.lutFrom !== '' || errors.lutTo !== '';
    
    return !hasGstinLengthError && !hasLutErrors;
  };

  const resetAll = () => {
    setStep(0);
    setButtonLoading(false);
    setLoading(false);
    setError('');
    setSelectedOption('FINANCIAL');
    setSelectedYear(0);
    setFyAlreadyExists(false);
    setConfirmationModal(false);
    setIsCompanyPreferenceCheck(false);
    setTotalTurnOver(0);
    setEnableEInvoice(false);
    setGstDetailsValidationErrors({
      gstin: '',
      lutNumber: '',
      lutFrom: '',
      lutTo: '',
    });
    setState({
      startYear: 0,
      endyear: 0,
      startDate: 0,
      endDate: 0,
      financialYearFromId: 0,
      financialYearToId: 0,
      fyPreview: '',
      companyDetails: null,
      companyEntity: null,
      companyAddressEntity: null,
      transferClosingbalance: false,
    });
  };

  return {
    step,
    error,
    next,
    prev,
    goTo,
    state,
    buttonLoading,
    FINANCIAL_YEAR_OPTIONS,
    gstDetailsValidationErrors,
    validateGSTDetails,
    selectedOption,
    setSelectedOption,
    selectedYear,
    setSelectedYear,
    fyAlreadyExists,
    loading,
    handleChangeGSTDetails,
    handleCancel,
    handleSubmit,
    handleConfirmOrCancel,
    confirmationModal,
    handleOnBlurGSTDetails,
    isCompanyPreferenceCheck,
    totalTurnOver,
    formErrors,
  };
};
